// apps/web/src/routes/(showcase)/showcase/neighborhood.tsx
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useCallback, useState } from "react";
import { PoiList } from "~/components/showcase/map/poi-list";
import { PoiMap } from "~/components/showcase/map/poi-map";
import { ShowcasePageHeader } from "~/components/showcase/showcase-page-header";
import { seo } from "~/lib/seo";
import {
  getPoiHex,
  POI_CATEGORIES,
  type PoiCategory,
} from "~/lib/showcase/poi-data";
import { useSkin } from "~/lib/showcase/skin";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(showcase)/showcase/neighborhood")({
  component: NeighborhoodPage,
  head: () => {
    const title = m.meta_showcase_neighborhood_title();
    const description = m.meta_showcase_neighborhood_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase/neighborhood",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        pageName: title,
        path: "/showcase/neighborhood",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
});

const CHIP_BASE =
  "sc-touch inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sc-accent)]";
const CHIP_ACTIVE =
  "border-[var(--sc-fg)] bg-[var(--sc-fg)] text-[var(--sc-bg)]";
const CHIP_INACTIVE =
  "border-[var(--sc-border)] bg-[var(--sc-surface)] text-[var(--sc-muted)] hover:text-[var(--sc-fg)]";

function NeighborhoodPage() {
  const { skin } = useSkin();
  const [filter, setFilter] = useState<PoiCategory | "all">("all");
  const [activeId, setActiveId] = useState<string | null>(null);

  const handleSelect = useCallback((id: string) => {
    setActiveId((prev) => (prev === id ? null : id));
  }, []);

  const handleFilter = useCallback((next: PoiCategory | "all") => {
    setFilter(next);
    setActiveId(null);
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <ShowcasePageHeader
        eyebrow={m.showcase_map_eyebrow()}
        lead={m.showcase_map_lead()}
        title={m.showcase_map_title()}
      />

      <fieldset className="mb-6 flex flex-wrap gap-2 border-0 p-0">
        <legend className="sr-only">{m.showcase_map_filter_label()}</legend>
        <button
          aria-pressed={filter === "all"}
          className={`${CHIP_BASE} ${filter === "all" ? CHIP_ACTIVE : CHIP_INACTIVE}`}
          onClick={() => handleFilter("all")}
          type="button"
        >
          {m.showcase_map_filter_all()}
        </button>
        {POI_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <button
              aria-pressed={filter === cat.id}
              className={`${CHIP_BASE} ${filter === cat.id ? CHIP_ACTIVE : CHIP_INACTIVE}`}
              key={cat.id}
              onClick={() => handleFilter(cat.id)}
              type="button"
            >
              <Icon
                aria-hidden="true"
                className="size-3.5 shrink-0"
                style={{ color: getPoiHex(cat.id, skin) }}
              />
              {cat.getLabel()}
            </button>
          );
        })}
      </fieldset>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)]">
        <PoiMap activeId={activeId} filter={filter} onSelect={handleSelect} />
        <PoiList activeId={activeId} filter={filter} onSelect={handleSelect} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[var(--sc-muted)] text-sm">
        <span>{m.showcase_map_concierge_hint()}</span>
        <Link
          className="inline-flex items-center gap-1 font-medium text-[var(--sc-accent)] underline-offset-2 hover:underline"
          to="/showcase/concierge"
        >
          {m.showcase_map_concierge_cta()}
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
